/*
  Carnevore Mountain order deadline bar.
  <cm-order-countdown> counts down to midnight at the end of the cutoff day in the store's time zone,
  whatever the visitor's own time zone, then rolls over to the next week.
*/
if (!customElements.get('cm-order-countdown')) {
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const DAY_MS = 86400000;
  const formatters = new Map();

  const formatterFor = (timeZone) => {
    if (!formatters.has(timeZone)) {
      let formatter = null;
      try {
        formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          hourCycle: 'h23',
          weekday: 'short',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        });
      } catch (error) {
        formatter = null;
      }
      formatters.set(timeZone, formatter);
    }
    return formatters.get(timeZone);
  };

  // The wall-clock date and time in the time zone at an instant, as UTC fields.
  const wallClock = (instant, timeZone) => {
    const formatter = formatterFor(timeZone);
    const date = new Date(instant);
    if (!formatter) {
      return {
        wall: Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()),
        weekday: date.getDay(),
      };
    }
    const parts = {};
    formatter.formatToParts(date).forEach((part) => {
      parts[part.type] = part.value;
    });
    return {
      wall: Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour) % 24, Number(parts.minute), Number(parts.second)),
      weekday: WEEKDAYS.indexOf(parts.weekday),
    };
  };

  // Milliseconds from now until midnight at the end of the cutoff day, allowing for daylight saving changes.
  const timeUntilCutoff = (cutoffDay, timeZone) => {
    const now = Date.now();
    const current = wallClock(now, timeZone);
    const startOfToday = current.wall - (current.wall % DAY_MS);
    const daysAhead = (cutoffDay - current.weekday + 7) % 7;
    const endWall = startOfToday + (daysAhead + 1) * DAY_MS;
    const offsetNow = current.wall - Math.floor(now / 1000) * 1000;
    const guess = endWall - offsetNow;
    const offsetThen = wallClock(guess, timeZone).wall - guess;
    return Math.max(0, endWall - offsetThen - now);
  };

  const pad = (value) => String(value).padStart(2, '0');

  class CmOrderCountdown extends HTMLElement {
    connectedCallback() {
      this.cutoffDay = Number(this.dataset.cutoffDay || 0);
      this.timeZone = this.dataset.timeZone || 'America/Toronto';
      this.numbers = {};
      this.querySelectorAll('[data-unit]').forEach((node) => {
        this.numbers[node.dataset.unit] = node;
      });
      this.tick = this.tick.bind(this);
      this.tick();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
    }

    tick() {
      const remaining = timeUntilCutoff(this.cutoffDay, this.timeZone);
      const total = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(total / 86400),
        hours: Math.floor((total % 86400) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60,
      };
      Object.keys(values).forEach((unit) => {
        const node = this.numbers[unit];
        const text = pad(values[unit]);
        if (node && node.textContent !== text) node.textContent = text;
      });
      // Wake just after the next whole second so the seconds never skip.
      this.timer = window.setTimeout(this.tick, 1000 - (Date.now() % 1000) + 20);
    }
  }

  customElements.define('cm-order-countdown', CmOrderCountdown);
}
